import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import NavBar from '../LandingPage/NavBar'
import useGlobalTheme from '../../hooks/useGlobalTheme'

function ResetPassword() {
	const navigate = useNavigate()
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const { token } = useParams()
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [message, setMessage] = useState('')
	const [errorMessage, setErrorMessage] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)

	useEffect(() => {
		if (!token) {
			setErrorMessage('Invalid reset link')
		}
	}, [token])

	const handleSubmit = async e => {
		e.preventDefault()

		if (!token) {
			setErrorMessage('Invalid reset link')
			return
		}

		// Validate passwords
		if (!password.trim()) {
			setErrorMessage('Please enter a new password')
			return
		}

		if (password.length < 6) {
			setErrorMessage('Password must be at least 6 characters long')
			return
		}

		if (password !== confirmPassword) {
			setErrorMessage('Passwords do not match')
			return
		}

		setIsSubmitting(true)
		setErrorMessage('')
		setMessage('')

		try {
			const response = await fetch(`/api/auth/reset-password/${token}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ password }),
			})

			const data = await response.json()

			if (response.ok) {
				setMessage('Password has been reset successfully')
				setTimeout(() => {
					navigate('/login')
				}, 2000)
			} else {
				setErrorMessage(data.message || 'An error occurred')
			}
		} catch (error) {
			console.error('Error resetting password:', error)
			setErrorMessage('An error occurred while resetting your password')
		} finally {
			setIsSubmitting(false)
		}
	}

	if (!token) {
		return (
			<>
				<NavBar isNotLandingPage={true} />
				<div className={`flex justify-center items-start pt-10 pb-10 min-h-[calc(100vh-64px)] ${isLight ? 'bg-white' : 'bg-[#15202b]'}`}>
					<div className={`p-8 rounded-lg w-full max-w-md ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg text-gray-900' : 'bg-gray-800 ring-1 ring-white/10 shadow-xl text-white'}`}>
						<h2 className="text-2xl font-semibold mb-6 text-center">
							Invalid Reset Link
						</h2>
						<p className={`text-center mb-6 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
							The password reset link is invalid or has expired.
						</p>
						<div className="text-center">
							<a href="/forgot-password" className="text-sky-400 hover:text-sky-300 hover:underline">
								Request a new reset link
							</a>
						</div>
					</div>
				</div>
			</>
		)
	}

	return (
		<>
			<NavBar isNotLandingPage={true} />
			<div className={`flex justify-center items-start pt-10 pb-10 min-h-[calc(100vh-64px)] ${isLight ? 'bg-white' : 'bg-[#15202b]'}`}>
				<div className={`p-8 rounded-lg w-full max-w-md ${isLight ? 'bg-white ring-1 ring-gray-200 shadow-lg text-gray-900' : 'bg-gray-800 ring-1 ring-white/10 shadow-xl text-white'}`}>
					<h2 className="text-2xl font-semibold mb-6 text-center">
						Reset Password
					</h2>

					<p className={`text-sm mb-6 text-center ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
						Enter your new password below
					</p>

					{errorMessage && (
						<div className={`text-center mb-4 ${isLight ? 'text-red-600' : 'text-red-400'}`}>{errorMessage}</div>
					)}

					{message && (
						<div className={`text-center mb-4 ${isLight ? 'text-green-600' : 'text-green-400'}`}>{message}</div>
					)}

					<form onSubmit={handleSubmit}>
						<div className="space-y-4">
							<div>
								<label
									htmlFor="password"
									className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									New Password
								</label>
								<input
									type="password"
									id="password"
									value={password}
									onChange={e => setPassword(e.target.value)}
									className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isLight ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400' : 'bg-gray-700 border border-gray-600 text-white'}`}
									disabled={isSubmitting}
									minLength="6"
									required
								/>
							</div>

							<div>
								<label
									htmlFor="confirmPassword"
									className={`block text-sm font-medium ${isLight ? 'text-gray-700' : 'text-gray-300'}`}
								>
									Confirm New Password
								</label>
								<input
									type="password"
									id="confirmPassword"
									value={confirmPassword}
									onChange={e => setConfirmPassword(e.target.value)}
									className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isLight ? 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400' : 'bg-gray-700 border border-gray-600 text-white'}`}
									disabled={isSubmitting}
									minLength="6"
									required
								/>
							</div>
						</div>

						<div className="mt-6">
							<button
								type="submit"
								className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#1d9bf0] hover:bg-[#1a8cd8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${isLight ? 'focus:ring-offset-white' : 'focus:ring-offset-gray-800'}`}
								disabled={isSubmitting}
							>
								{isSubmitting ? 'Resetting...' : 'Reset Password'}
							</button>
						</div>
					</form>

					<div className={`mt-6 text-sm text-center ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
						Remember your password?{' '}
						<a href="/login" className="text-sky-400 hover:text-sky-300 hover:underline">
							Back to Login
						</a>
					</div>
				</div>
			</div>
		</>
	)
}

export default ResetPassword